import { toHiragana } from 'wanakana';
import { isKanaOnly, stripPunctuation } from './input';

/**
 * Normalizes a raw answer the way every session page already does: drop
 * whitespace and punctuation, then convert a trailing bare "n" to ん (wanakana's
 * IME mode leaves it pending until another key arrives, so a submitted answer
 * would otherwise end in a latin character).
 */
export const normalizeAnswer = (raw) => {
  const stripped = stripPunctuation(String(raw ?? '').replace(/[\s\n]/g, ''));
  return stripped.endsWith('n') ? stripped.replace(/n$/, 'ん') : stripped;
};

/**
 * Builds the list of answers accepted for a word in the given input mode.
 *
 * `acceptable_forms` is a single flat list of "other ways to write this word",
 * mirroring the field on sentences.words[]. It is split by character class at
 * read time so each mode keeps its meaning: kana-only entries are alternate
 * readings, the rest are alternate written forms. When the headword is itself
 * kana-only (コンピュータ) the written form *is* the reading, so every alternate
 * counts as both.
 */
export const buildWordForms = (word, inputMode = 'mixed') => {
  if (!word) return [];
  const alternates = Array.isArray(word.acceptable_forms) ? word.acceptable_forms.filter(Boolean) : [];
  const headwordIsKana = isKanaOnly(word.word);
  const written = headwordIsKana ? alternates : alternates.filter((form) => !isKanaOnly(form));
  const readings = headwordIsKana ? alternates : alternates.filter(isKanaOnly);

  if (inputMode === 'kanji') return [word.word, ...written].filter(Boolean);
  if (inputMode === 'kana') return [word.kana, ...readings].filter(Boolean);
  return [word.word, word.kana, ...alternates].filter(Boolean);
};

/**
 * Transliterates kana to hiragana for comparison WITHOUT touching vowel length.
 *
 * wanakana folds the katakana長音 mark into a vowel by default, which both
 * over-matches (ビール → びいる) and under-matches: コンピュータ would become
 * こんぴゅうた while an already-hiragana こんぴゅーた stays put, so the two would
 * no longer compare equal. `convertLongVowelMark: false` keeps ー intact, giving
 * pure script folding.
 */
const foldScript = (str) => toHiragana(str, { convertLongVowelMark: false });

/**
 * Grades a typed answer against a word.
 *
 * Outside strict-kanji mode, two kana-only strings are compared script
 * -insensitively (こんぴゅーた matches コンピュータ), which is what sentence
 * grading already gets for free by carrying both `hiragana` and `katakana`.
 * Strict-kanji mode stays byte-exact so it keeps meaning what it claims.
 *
 * Long vowels are deliberately NOT normalized: ビル (building) and ビール (beer)
 * are different words, so ー equivalence has to come from curated
 * `acceptable_forms` data rather than from a rule.
 */
export const checkWordAnswer = (word, rawInput, inputMode = 'mixed') => {
  const input = normalizeAnswer(rawInput);
  const forms = buildWordForms(word, inputMode);
  const scriptInsensitive = inputMode !== 'kanji' && isKanaOnly(input);

  const isCorrect = forms.some((form) => {
    if (form === input) return true;
    return scriptInsensitive && isKanaOnly(form) && foldScript(form) === foldScript(input);
  });

  // Shown in the "Expected: …" tooltip on a wrong answer. Kana mode asks for the
  // reading; the other two ask for the written form.
  const expected = inputMode === 'kana' ? word?.kana : word?.word;

  return { input, isCorrect, expected };
};

/**
 * Builds the forms accepted for one word *as it appears in a sentence*.
 *
 * Unlike a standalone word, a sentence occurrence carries its own conjugated
 * `sentence_form` plus explicit `hiragana`/`katakana`, so the modes are keyed off
 * those rather than off character class. Kana mode deliberately offers only the
 * two readings — `acceptable_forms` may contain kanji.
 */
export const buildSentenceWordForms = (word, inputMode = 'mixed') => {
  if (!word) return [];
  const alternates = Array.isArray(word.acceptable_forms) ? word.acceptable_forms : [];
  if (inputMode === 'kanji') return [...alternates, word.sentence_form].filter(Boolean);
  if (inputMode === 'kana') return [word.hiragana, word.katakana].filter(Boolean);
  return [...alternates, word.sentence_form, word.hiragana, word.katakana].filter(Boolean);
};

/**
 * Finds a way to consume the whole input by matching each word in order.
 *
 * A plain left-to-right greedy scan is not sufficient: candidate forms can be
 * prefixes of one another (a word with `sentence_form: 'して'` and
 * `acceptable_forms: ['し']` followed by `いる`), so committing to the first
 * match can consume too little and derail every word after it — marking the
 * exactly-correct sentence wrong. Trying each candidate and backtracking is
 * exact, and memoising failed (word, offset) states keeps it linear in practice.
 *
 * Returns the winning list of forms, plus the deepest partial match reached, so
 * a failed answer can still report where it went wrong.
 */
const matchWordSequence = (words, input, inputMode) => {
  const failed = new Set();
  let best = { wordIndex: 0, offset: 0, forms: [] };

  const walk = (wordIndex, offset, forms) => {
    if (offset > best.offset || (offset === best.offset && wordIndex > best.wordIndex)) {
      best = { wordIndex, offset, forms };
    }
    if (wordIndex === words.length) return offset === input.length ? forms : null;

    const stateKey = `${wordIndex}:${offset}`;
    if (failed.has(stateKey)) return null;

    for (const form of buildSentenceWordForms(words[wordIndex], inputMode)) {
      if (!input.startsWith(form, offset)) continue;
      const solved = walk(wordIndex + 1, offset + form.length, [...forms, form]);
      if (solved) return solved;
    }

    failed.add(stateKey);
    return null;
  };

  return { solution: walk(0, 0, []), best };
};

/**
 * Grades a typed sentence, consuming the input word by word from the left.
 *
 * Returns the per-segment `inputResults` that InputSection renders — `correct`
 * segments in green, a `correct: false` segment carrying the `expected` form for
 * its tooltip, and an `unchecked` tail in grey for input past the first mistake.
 * Matching stops at the first word that fails; see PARTIAL_HIGHLIGHTING_ANALYSIS.md
 * for why the remainder is not re-aligned.
 */
export const checkSentenceAnswer = (sentence, rawInput, inputMode = 'mixed') => {
  const words = Array.isArray(sentence?.words) ? sentence.words : [];
  const input = normalizeAnswer(rawInput);

  if (words.length === 0) {
    return { inputResults: input ? [{ text: input, correct: false }] : [], isCorrect: false };
  }

  const { solution, best } = matchWordSequence(words, input, inputMode);
  if (solution) {
    return { inputResults: solution.map((form) => ({ text: form, correct: true })), isCorrect: true };
  }

  // No full match. Report the deepest partial: everything it consumed is green,
  // and the first thing it could not consume is the mistake.
  const inputResults = best.forms.map((form) => ({ text: form, correct: true }));
  const remaining = input.slice(best.offset);

  if (remaining.length > 0) {
    if (best.wordIndex >= words.length) {
      // Every word matched but the answer kept going — trailing input is wrong.
      inputResults.push({ text: remaining, correct: false });
    } else {
      const word = words[best.wordIndex];
      const kanaForm = word.hiragana || word.katakana;
      const kanjiForm = word.sentence_form || word.hiragana;
      // In mixed mode, guess which script the user was aiming for from what they
      // actually typed, so the tooltip suggests a comparable form.
      const expectedWord =
        inputMode === 'kana'
          ? kanaForm
          : inputMode === 'mixed'
            ? (isKanaOnly(remaining.slice(0, kanaForm?.length || 1)) ? kanaForm : kanjiForm)
            : kanjiForm;
      const errorLength = Math.min(expectedWord?.length || 1, remaining.length);
      inputResults.push({ text: remaining.slice(0, errorLength), correct: false, expected: expectedWord });
      const uncheckedPortion = remaining.slice(errorLength);
      if (uncheckedPortion.length > 0) {
        inputResults.push({ text: uncheckedPortion, unchecked: true });
      }
    }
  }

  return { inputResults, isCorrect: false };
};
